import { FaEyeSlash, FaEye, FaClock } from "react-icons/fa";
import { Tag } from "@/frontend/components/common";
import type { PoolConfig, ScoringRuleType, DeadlineConfig } from "@/shared/dto";

interface PoolRulesTabProps {
  poolConfig?: PoolConfig | null;
}

const RULE_TYPE_LABELS: Record<string, string> = {
  exact_score_unique: "Marcador exacto único",
  exact_score: "Marcador exacto",
  winner_or_draw: "Ganador o empate",
  winner: "Ganador del partido",
  bracket_winner: "Ganador en eliminatoria",
  unique_bonus: "Bono por acierto único",
  goal_difference: "Diferencia de goles",
  home_goals_exact: "Goles local exactos",
  away_goals_exact: "Goles visitante exactos",
};

const PRIVACY_LABELS: Record<string, { label: string; icon: JSX.Element }> = {
  always: { label: "Siempre puedes ver las predicciones de los demás", icon: <FaEye /> },
  after_lock: {
    label: "Las predicciones de los demás se muestran cuando ya no puedes editar la tuya",
    icon: <FaEye />,
  },
  never: { label: "No puedes ver las predicciones de los demás participantes", icon: <FaEyeSlash /> },
};

function ruleLabel(type: ScoringRuleType | string): string {
  return RULE_TYPE_LABELS[type] || type;
}

function formatDeadline(deadline?: DeadlineConfig): string | null {
  if (!deadline) return null;
  const parts: string[] = [];
  if (deadline.days) parts.push(`${deadline.days} día${deadline.days === 1 ? "" : "s"}`);
  if (deadline.hours) parts.push(`${deadline.hours} hora${deadline.hours === 1 ? "" : "s"}`);
  if (deadline.minutes) parts.push(`${deadline.minutes} minuto${deadline.minutes === 1 ? "" : "s"}`);
  if (parts.length === 0) return null;
  return `${parts.join(", ")} antes del inicio del partido`;
}

export function PoolRulesTab({ poolConfig }: PoolRulesTabProps) {
  if (!poolConfig) {
    return <p className="text-gray-500 text-center py-8">No hay reglas configuradas para este grupo.</p>;
  }

  const { resultRules, bonusRules, defaultPoints, predictionDeadline } = poolConfig.scoring;
  const privacy = poolConfig.privacity?.showOthersPredictions
    ? PRIVACY_LABELS[poolConfig.privacity.showOthersPredictions]
    : null;
  const deadlineText = formatDeadline(predictionDeadline);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-gray-800 mb-2">Puntos por resultado</h4>
        <div className="space-y-2">
          {resultRules.map((rule, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-white">
              <span className="text-gray-700">{ruleLabel(rule.type)}</span>
              <Tag color="gold">+{rule.points} pts</Tag>
            </div>
          ))}
        </div>
      </div>

      {bonusRules.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">Puntos extra (bonos)</h4>
          <div className="space-y-2">
            {bonusRules.map((rule, idx) => (
              <div key={idx} className="flex flex-col gap-1 p-3 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{ruleLabel(rule.type)}</span>
                  <Tag color="purple">+{rule.points} pts</Tag>
                </div>
                {rule.requires && rule.requires.length > 0 && (
                  <span className="text-xs text-gray-400">
                    Requiere: {rule.requires.map(ruleLabel).join(", ")}
                  </span>
                )}
                {rule.condition && (
                  <span className="text-xs text-gray-400">
                    Condición: {ruleLabel(rule.condition.type)}
                    {rule.condition.isUniqueHit ? " (acierto único)" : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="font-semibold text-gray-800 mb-2">Otros detalles</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-white">
            <span className="text-gray-700">Puntos por defecto (sin acierto)</span>
            <Tag>{defaultPoints} pts</Tag>
          </div>
          {deadlineText && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-100 bg-white text-gray-700">
              <FaClock className="text-gray-400" />
              <span>Las predicciones se cierran {deadlineText}</span>
            </div>
          )}
          {privacy && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-100 bg-white text-gray-700">
              {privacy.icon}
              <span>{privacy.label}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
